import { noop } from "lodash-es";
import { createPinia } from "pinia";
import "the-new-css-reset/css/reset.css";
import { createApp } from "vue";

import { setActionTracker } from "@/action/actions";
import { trackEvent } from "@/analytics/track";
import { autoResetPlugin } from "@/autoResetPlugin";
import AppBase from "@/components/AppBase/AppBase.vue";
import type { IntercomStatic } from "@/declare/windowIntercom";
import { autofocus } from "@/directives/autofocus";
import { boardRegion } from "@/directives/boardRegion";
import { focusTrap } from "@/directives/focusTrap";
import { keyboardNavigation } from "@/directives/keyboardNavigation";
import { ownedCards } from "@/directives/ownedCards";
import { scrollableOnBoard } from "@/directives/scrollableOnBoard";
import { initConsoleSentry, initRealSentry } from "@/error/sentry";
import { initConnectionListeners, initErrorListeners } from "@/globalListeners";
import { setLanguage } from "@/language";
import { router } from "@/router/router";
import { assignShortcuts } from "@/shortcuts";
import { useClientSettingsStore } from "@/store/clientSettings";
import "@/styles/global/root-theme.scss";
import { initSearchBoardSync } from "@/store/search/searchBoardSync";
import { initSearchUrlSync } from "@/store/search/searchUrlSync";
import { initSignalSync } from "@/store/search/signalSync";
import { useStatisticsStore } from "@/store/statistics";
import { storesPlugin } from "@/storesPlugin";
import { i18n } from "@/translations/i18n";
import {
  disableDefaultContextMenu,
  disableImageDragging,
  preventSpacebarClickInterference,
} from "@/utils/dom/globalEvents";
import { wrapMutationObserver } from "@/utils/dom/mutationObserver";
import { environment, isCloud, sentryDsn, sentryLogBreadcrumbs } from "@/utils/env/Environment";
import { broadcastFeatures } from "@/utils/env/feature";

import { showLogo } from "./console";
import { registerCopyPaste } from "./copyPaste";
import { registerGlobalActions } from "./globalActions";

wrapMutationObserver();

broadcastFeatures();
setActionTracker(trackEvent);

const app = createApp(AppBase)
  .use(router)
  .use(createPinia().use(storesPlugin).use(autoResetPlugin))
  .use(i18n)
  .directive("focus-trap", focusTrap())
  .directive("autofocus", autofocus())
  .directive("scrollable-on-board", scrollableOnBoard())
  .directive("owned-cards", ownedCards())
  .directive("keyboard-nav", keyboardNavigation())
  .directive("board-region", boardRegion());

void (
  isCloud && sentryDsn
    ? initRealSentry(app, sentryDsn, environment)
    : initConsoleSentry(sentryLogBreadcrumbs)
).finally(() => {
  initErrorListeners(app);
  app.mount("#app").$watch("$route.query.lang", setLanguage);
});

useClientSettingsStore().init();
initConnectionListeners();

// real Intercom will be inited after login if enabled
// dummy init here, so we can just call Intercom without always checking if it's available or not
window.Intercom = noop as IntercomStatic;

showLogo();
assignShortcuts();
registerGlobalActions();
registerCopyPaste();
preventSpacebarClickInterference();
disableDefaultContextMenu();
disableImageDragging();
useStatisticsStore().start();
initSearchUrlSync();
initSearchBoardSync();
initSignalSync();
